import { ApiError } from '@lib/api';

import { reconciliationPollInterval, type ReadFailure } from '../model/donations-reconciliation';

import { bulkKeys } from './use-bulk-migrations';

import type { ReconciliationDocument } from '../schemas/donations-reconciliation';
import type { QueryClient } from '@tanstack/react-query';

/**
 * The query and mutation policy of the donations reconciliation panel (INI-0034 S7),
 * as plain functions: no React, so `reconciliation-query.test.ts` drives them with
 * a real `QueryClient` and no renderer (the web workspace has none, by design —
 * see `vitest.config.ts`). `use-donations-reconciliation.ts` only wires them.
 */

/** The target and the tenant are in the key: two environments, or two clients, never share it. */
export const reconciliationKey = (target: string, tenantId: string) =>
    [...bulkKeys.all, 'donations-reconciliation', target, tenantId] as const;

/** A read failure, as `panelState` reads it. */
export function readFailureOf(error: Error | null): ReadFailure | null {
    if (error === null) return null;
    return error instanceof ApiError
        ? { status: error.status, code: error.code ?? null }
        : { status: null, code: null };
}

/** A 4xx (module off, none yet, bad id) is an answer, never retried; anything else once. */
export function reconciliationRetry(failureCount: number, error: Error): boolean {
    return !(error instanceof ApiError && error.status < 500) && failureCount < 1;
}

/**
 * `refetchInterval`: every 5 s while the cached document is in flight, and never
 * again after a 4xx, whatever the cached document still says.
 */
export function reconciliationRefetchInterval(
    state: {
        readonly data: ReconciliationDocument | undefined;
        readonly error: Error | null;
    },
    now: number,
): number | false {
    return reconciliationPollInterval(state.data, readFailureOf(state.error), now);
}

/** A request answered: it IS the latest now, so it replaces the cached read. */
export function afterRequestSucceeded(
    client: QueryClient,
    key: readonly unknown[],
    document: ReconciliationDocument,
): void {
    client.setQueryData(key, document);
}

/**
 * A request refused or failed. A `409 RECONCILIATION_ALREADY_RUNNING` means another
 * request is in flight: re-read, so the panel shows that one. Any other failure
 * re-reads too, which is harmless and keeps the panel on what the runner holds.
 */
export function afterRequestFailed(client: QueryClient, key: readonly unknown[]): Promise<void> {
    return client.invalidateQueries({ queryKey: key });
}
